import React, { useMemo, useCallback, useState, useEffect } from "react";
import type { User } from "firebase/auth";
import { AuthContext, AuthContextType } from "@rapid/contexts/AuthContext";
import type { AuthAdapter } from "./AuthAdapter";

interface EmbeddedAuthBridgeProps {
    adapter?: AuthAdapter;
    children: React.ReactNode;
}

export function EmbeddedAuthBridge({ adapter, children }: EmbeddedAuthBridgeProps) {
    const [user, setUser] = useState<User | null>(adapter?.getCurrentUser() ?? null);
    const [loading, setLoading] = useState(!adapter?.getCurrentUser());
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!adapter) return;
        return adapter.onAuthStateChanged((u) => {
            setUser(u);
            setLoading(false);
        });
    }, [adapter]);

    const hasUser = adapter ? adapter.isSessionActive() : !!user;

    const noop = useCallback(async () => null as any, []);
    const noopVoid = useCallback(async () => {}, []);
    const noopBool = useCallback(async () => false, []);

    const value = useMemo<AuthContextType>(
        () => ({
            user,
            claims: null,
            apiAuthenticated: hasUser,
            activeSession: hasUser,
            initialLoadComplete: !loading,
            loading,
            setLoading,
            error,
            setError,
            logout: noopVoid,
            loginWithEmail: noop,
            signUpWithEmail: noop,
            sendLoginLink: noopBool,
            completeLoginWithEmailLink: noop,
            completeLoginWithEmailLinkAndName: noop,
            loginWithGoogle: noop,
            loginWithGithub: noop,
            emailForSignIn: null,
            setEmailForSignIn: () => {},
            emailLinkSent: false,
            pendingAuthInfo: false,
            isAdmin: false,
            hasWorkspaceAccess: () => true,
            isLoginModalOpen: false,
            requireLogin: () => {},
            closeLoginModal: () => {},
            guestUid: null,
            guestAuthInitialized: true,
            initializeGuestAuth: noopVoid
        }),
        [user, hasUser, loading, error, noop, noopVoid, noopBool]
    );

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
